/** 运行与回放（规格 `docs/design/screens/replay.md`，G5）。
 *
 * 运行列表把回测与纸上盘两种 `mode` 列在同一张表（同源执行）；点一行看运行详情（可回放面：
 * 快照、口径指纹、受理信号、账目指纹）。盈亏以权益口径 `total_pnl` 为主，现金变动只作对照。
 * 跑一天 / 补跑的触发人读控制事件台账（`kind=run`，#4752）。
 * 同日重跑指纹读运行详情的 `reruns[]`；回放对照 `POST /replay` 发起、`GET /replay/{replay_id}` 读结果。
 * 一致与否、决策差异都由后端判定，前端不比指纹（#4752）。
 * 「同源跑一遍」`POST /runs`（带操作人 / 理由），触发留痕读控制事件台账里带 `run_id` 的 `kind=run`（E5 #5008）。
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isRetryable } from "../../api/errors";
import { useEquity, useHaltState, useRunDetail, useRuns } from "../../api/queries";
import type { RunDetail, RunRecord } from "../../api/types";
import { Card, Kv, Lede, Note, PageHead } from "../../components/Card";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { money, short, signedMoney, tone, ts } from "../../lib/format";
import {
  dayVerdict,
  readReplay,
  readRunTriggers,
  readSameSourceTriggers,
  SAME_SOURCE_RUN_EXTRA,
  SAME_SOURCE_RUN_PATH,
  rerunVerdict,
  startReplay,
  type ReplayResult,
  type RunTrigger,
  type SameSourceTrigger,
} from "../../lib/paperReplay";
import { ActionForm, ScreenState } from "../p2/Common";

const MODES = ["全部", "回测", "纸上盘"] as const;
const PAPER_RUN = /^paper-(.+)-(\d{4}-\d{2}-\d{2})$/;

/** 纸上盘运行的归属策略：后端给 `strategy_id` 就用；旧索引没有时从 `paper-<sid>-<date>` 读。 */
function strategyOf(run: RunRecord): string | null {
  if (run.strategy_id) return run.strategy_id;
  const m = PAPER_RUN.exec(run.run_id);
  return m ? (m[1] ?? null) : null;
}

function Pnl({ value }: { value: number | undefined }): React.ReactNode {
  return <span className={tone(value)}>{signedMoney(value)}</span>;
}

export function PaperReplayScreen(): React.ReactNode {
  const [mode, setMode] = useState<(typeof MODES)[number]>("全部");
  const [strategy, setStrategy] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const runs = useRuns();
  const halt = useHaltState();
  const anyHalted = halt.data ? halt.data.halted || Object.values(halt.data.strategies).some((s) => s.halted) : false;
  const strategies = useMemo(() => {
    const ids = new Set<string>();
    for (const run of runs.data ?? []) {
      const sid = strategyOf(run);
      if (sid) ids.add(sid);
    }
    return [...ids].sort();
  }, [runs.data]);

  return (
    <>
      <PageHead title="运行与回放" sub="GET /runs · GET /runs/{run_id} · GET /paper/equity" />
      <Lede>
        每条策略每天跑了没有、跑出什么账；同一快照与信号重放能否得到同一本账。回测与纸上盘走同一条下单路径，列在同一张表里；
        盈亏以权益口径为主，现金变动只作对照。
      </Lede>
      {anyHalted ? <Note tone="bad">停机停手：存在停机策略，跑一天会被服务端拒绝（预演 dry_run 除外）。</Note> : null}
      <div className="fieldrow" style={{ marginBottom: 12 }}>
        <label className="f">
          <span>模式</span>
          <select value={mode} onChange={(e) => setMode(e.target.value as (typeof MODES)[number])}>
            {MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          <span>策略</span>
          <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <option value="">全部策略</option>
            {strategies.map((sid) => (
              <option key={sid} value={sid}>
                {sid}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="split wide">
        <ScreenState query={runs} what="运行列表">
          {(rows) => {
            const shown = rows
              .filter((x) => mode === "全部" || x.mode === mode)
              .filter((x) => !strategy || strategyOf(x) === strategy)
              .sort((a, b) => b.ts_ns - a.ts_ns);
            return (
              <Card title="运行列表" sub={`${shown.length} 次 · 点一行看详情`} nopad>
                <Table
                  tall
                  cols={[
                    { label: "运行", mono: true },
                    { label: "模式" },
                    { label: "时间" },
                    { label: "订单", num: true },
                    { label: "成交", num: true },
                    { label: "总盈亏（权益口径）", num: true },
                    { label: "现金变动（对照）", num: true },
                    { label: "对账" },
                  ]}
                  rows={shown.map((x) => ({
                    key: x.run_id,
                    selected: x.run_id === selected,
                    onClick: () => setSelected(x.run_id),
                    cells: [
                      x.run_id,
                      <span style={{ whiteSpace: "nowrap" }}>{x.mode}</span>,
                      <span style={{ whiteSpace: "nowrap" }}>{ts(x.ts_ns)}</span>,
                      x.orders,
                      x.fills,
                      <Pnl value={x.total_pnl ?? x.realized_pnl} />,
                      <Pnl value={x.cash_change} />,
                      x.balanced ? <span className="pos">平</span> : <span className="neg">不平</span>,
                    ],
                  }))}
                  empty={rows.length === 0 ? "还没有运行记录" : strategy ? "该策略还没有纸上盘运行" : "该模式下没有运行"}
                />
              </Card>
            );
          }}
        </ScreenState>
        <RunDetailCard runId={selected} />
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <EquityCard strategy={strategy} />
        <ActionForm
          path="/paper/run"
          title="跑一天 / 补跑"
          fields={[
            { name: "trade_date", label: "交易日", type: "date" },
            { name: "catch_up", label: "补跑（从最后已处理日的下一交易日补到交易日）", type: "checkbox", required: false },
            { name: "dry_run", label: "预演（dry_run，不落账）", type: "checkbox", required: false },
            { name: "operator", label: "操作人" },
            { name: "reason", label: "理由" },
          ]}
        />
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <ReplayCard strategies={strategies} halted={anyHalted} />
        <TriggerCard />
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <ActionForm
          path={SAME_SOURCE_RUN_PATH}
          title="同源跑一遍"
          fields={[
            { name: "operator", label: "操作人" },
            { name: "reason", label: "理由" },
          ]}
          extra={SAME_SOURCE_RUN_EXTRA}
          note={
            <>
              用当前数据快照与信号按同一条下单路径跑一次回测（`POST /runs`），完成后运行列表多一条。
              {anyHalted ? "停机中：服务端会拒绝（409）。" : null}
            </>
          }
        />
        <SameSourceTriggerCard />
      </div>
    </>
  );
}

/** 同源跑一遍的触发留痕（`GET /paper/events` 里带 `run_id` 的 `kind=run`，E5 #5008）。 */
function SameSourceTriggerCard(): React.ReactNode {
  const triggers = useQuery<SameSourceTrigger[], Error>({
    queryKey: ["replay", "same-source-triggers"],
    queryFn: readSameSourceTriggers,
    retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  });
  return (
    <ScreenState query={triggers} what="同源运行留痕">
      {(rows) => (
        <Card title="同源运行留痕" sub="GET /paper/events · kind=run · 带 run_id" nopad>
          <Table
            cols={[{ label: "受理时间" }, { label: "运行", mono: true }, { label: "来源" }, { label: "操作人" }, { label: "理由", wrap: true }]}
            rows={rows.map((row) => ({
              key: `${row.ts_ns}-${row.run_id}`,
              cells: [ts(row.ts_ns), row.run_id, row.source === "api" ? "接口（未带操作人）" : "操作人", row.operator ?? "—", row.reason ?? "—"],
            }))}
            empty="还没有同源运行的触发记录"
          />
        </Card>
      )}
    </ScreenState>
  );
}

function RunDetailCard({ runId }: { runId: string | null }): React.ReactNode {
  const detail = useRunDetail(runId);
  if (!runId) {
    return (
      <Card title="运行详情">
        <div className="empty">在左侧点一行，看这次运行的快照、口径指纹、受理信号与账目指纹。</div>
      </Card>
    );
  }
  return (
    <ScreenState query={detail} what="运行详情">
      {(d) => {
        const accepted = d.signals?.accepted.length;
        return (
          <Card title="运行详情" sub={d.run_id}>
            <Kv
              pairs={[
                ["模式", d.mode],
                ["策略 / 交易日", `${strategyOf(d) ?? "—"} · ${d.trade_date ?? "—"}`],
                ["数据快照", <code>{d.snapshot_id || "—"}</code>],
                ["撮合口径指纹", <code>{d.config_digest}</code>],
                ["账目指纹", <code>{short(d.ledger_digest, 16)}</code>],
                [
                  "受理信号",
                  d.signals
                    ? `受理 ${accepted} · 重复 ${d.signals.duplicates.length} · 拒收 ${d.signals.rejected.length}`
                    : "缺（该运行记录没有信号计数）",
                ],
                ["对账", d.reconciliation ? d.reconciliation.summary : d.balanced ? "平" : "不平"],
                ["手续费 / 滑点（成本项）", `${money(d.commissions)} / ${money(d.slippage_cost)}`],
                ["现金 / 起始资金", `${money(d.cash)} / ${money(d.starting_cash)}`],
                ["耗时", `${d.duration_s.toFixed(3)} 秒`],
              ]}
            />
            {accepted === 0 ? <Note>当天无受理信号（无信号日为常态，不是故障）。</Note> : null}
            <Reruns detail={d} />
          </Card>
        );
      }}
    </ScreenState>
  );
}

/** 同日重跑：历次账目指纹与触发人；一致与否只认后端的 `same_as_previous`。 */
function Reruns({ detail }: { detail: RunDetail }): React.ReactNode {
  if (detail.mode !== "纸上盘") return null;
  const reruns = detail.reruns;
  if (!reruns) return <Note>同日重跑：缺（该运行早于重跑留痕，旧产物当时已被覆盖）。</Note>;
  const verdict = rerunVerdict(reruns);
  return (
    <>
      <Note tone={reruns.some((r) => r.same_as_previous === false) ? "bad" : ""}>同日重跑：{verdict}</Note>
      {reruns.length > 1 ? (
        <Table
          cols={[{ label: "时间" }, { label: "账目指纹", mono: true }, { label: "同上一次" }, { label: "操作人" }, { label: "理由", wrap: true }, { label: "旧产物", mono: true }]}
          rows={reruns.map((r, i) => ({
            key: `${r.ts_utc}-${i}`,
            cls: r.same_as_previous === false ? "bad" : "",
            cells: [
              r.ts_utc,
              short(r.ledger_digest, 12),
              r.same_as_previous === null ? "—" : r.same_as_previous ? "一致" : "不一致",
              r.operator ?? "缺",
              r.reason ?? "缺",
              r.archive ?? "当前",
            ],
          }))}
        />
      ) : null}
    </>
  );
}

function EquityCard({ strategy }: { strategy: string }): React.ReactNode {
  const equity = useEquity(strategy);
  if (!strategy) {
    return (
      <Card title="权益表">
        <div className="empty">先选一条策略</div>
      </Card>
    );
  }
  return (
    <ScreenState query={equity} what="权益表">
      {(d) => (
        <Card title={`权益表 · ${d.strategy}`} nopad>
          <Table
            cols={[
              { label: "交易日" },
              { label: "现金", num: true },
              { label: "总盈亏（权益口径）", num: true },
              { label: "已实现（闭合）", num: true },
              { label: "现金变动（对照）", num: true },
              { label: "手续费", num: true },
              { label: "滑点", num: true },
            ]}
            rows={d.points.map((p) => ({
              key: p.date,
              cells: [
                p.date,
                money(p.cash),
                <Pnl value={p.total_pnl ?? p.pnl} />,
                <Pnl value={p.realized_pnl_closed} />,
                <Pnl value={p.cash_change} />,
                money(p.fees),
                money(p.slippage),
              ],
            }))}
            empty="该策略还没有权益行"
          />
        </Card>
      )}
    </ScreenState>
  );
}

/** 回放对照：`POST /replay` 发起，`GET /replay/{replay_id}` 轮询到结束。判等与比对全在后端，前端只显示。 */
function ReplayCard({ strategies, halted }: { strategies: string[]; halted: boolean }): React.ReactNode {
  const [form, setForm] = useState({ strategy_id: "", from: "", to: "", operator: "", reason: "" });
  const [replayId, setReplayId] = useState<string | null>(null);
  const client = useQueryClient();
  const start = useMutation({
    mutationFn: startReplay,
    onSuccess: (accepted) => {
      setReplayId(accepted.replay_id);
      void client.invalidateQueries({ queryKey: ["replay", "run-triggers"] });
    },
  });
  const result = useQuery<ReplayResult, Error>({
    queryKey: ["replay", "result", replayId],
    queryFn: () => readReplay(replayId as string),
    enabled: Boolean(replayId),
    refetchInterval: (q) => (q.state.data?.status === "running" ? 1_000 : false),
    retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  });
  const missing = Object.values(form).some((v) => !v.trim());
  const badRange = Boolean(form.from && form.to && form.from > form.to);
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  return (
    <Card title="回放对照" sub="POST /replay · GET /replay/{replay_id}">
      <div className="fieldrow">
        <label className="f">
          <span>strategy_id</span>
          <select value={form.strategy_id} onChange={set("strategy_id")}>
            <option value="">请选择</option>
            {strategies.map((sid) => (
              <option key={sid} value={sid}>
                {sid}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          <span>from</span>
          <input type="date" value={form.from} onChange={set("from")} />
        </label>
        <label className="f">
          <span>to（同一天即按日）</span>
          <input type="date" value={form.to} onChange={set("to")} />
        </label>
        <label className="f">
          <span>operator</span>
          <input value={form.operator} onChange={set("operator")} />
        </label>
        <label className="f">
          <span>reason</span>
          <input value={form.reason} onChange={set("reason")} />
        </label>
      </div>
      <button
        type="button"
        className="btn primary"
        disabled={missing || badRange || start.isPending || halted}
        onClick={() =>
          start.mutate({
            strategy_id: form.strategy_id,
            from: form.from,
            to: form.to,
            operator: form.operator.trim(),
            reason: form.reason.trim(),
          })
        }
      >
        {start.isPending ? "提交中…" : "回放"}
      </button>
      {halted ? <Note tone="bad">停机停手：回放同样走下单路径，服务端会拒绝（409）。</Note> : null}
      {badRange ? <Note tone="warn">区间非法：from 晚于 to。</Note> : null}
      {start.isError ? <Note tone="bad">{start.error instanceof Error ? start.error.message : String(start.error)}</Note> : null}
      {!replayId ? (
        <div className="empty">还没有回放记录</div>
      ) : (
        <QueryState status={result.status} error={result.error} what="回放结果" refetch={() => void result.refetch()}>
          {result.data ? <ReplayView data={result.data} /> : null}
        </QueryState>
      )}
    </Card>
  );
}

function ReplayView({ data }: { data: ReplayResult }): React.ReactNode {
  const head =
    data.status === "running"
      ? `回放中…已完成 ${data.days.length} 天`
      : data.status === "failed"
        ? `回放失败：${data.error ?? "—"}`
        : data.all_same === true
          ? "区间内每天的决策序列与账目指纹都与当时一致"
          : data.all_same === false
            ? "有交易日与当时记录不一致"
            : "没有不一致，但有交易日无法回放（原运行输入缺失）";
  const diffs = data.days.flatMap((d) => d.decision_diffs.map((x) => ({ day: d.trade_date, ...x })));
  return (
    <>
      <Note tone={data.status === "failed" || data.all_same === false ? "bad" : data.all_same === null ? "warn" : ""}>
        {data.replay_id} · {data.operator}：{data.reason} —— {head}
      </Note>
      <Table
        cols={[{ label: "交易日" }, { label: "结论", wrap: true }, { label: "原账目指纹", mono: true }, { label: "回放指纹", mono: true }, { label: "订单", num: true }, { label: "决策差异", num: true }]}
        rows={data.days.map((d) => ({
          key: d.trade_date,
          cls: d.same === false ? "bad" : "",
          cells: [d.trade_date, dayVerdict(d), short(d.original_ledger_digest, 12), short(d.replay_ledger_digest, 12), d.orders ?? "—", d.decision_diffs.length],
        }))}
        empty="区间内没有已完成的回放日"
      />
      {diffs.length ? (
        <Table
          cols={[{ label: "交易日" }, { label: "序号", num: true }, { label: "字段" }, { label: "当时" }, { label: "回放" }, { label: "订单", mono: true }]}
          rows={diffs.map((x, i) => ({
            key: `${x.day}-${x.index}-${x.field}-${i}`,
            cls: "bad",
            cells: [x.day, x.index, x.field, String(x.original ?? "—"), String(x.replay ?? "—"), x.client_order_id ?? "—"],
          }))}
        />
      ) : null}
    </>
  );
}

/** 写入动作留痕：跑一天 / 补跑被受理时落盘的触发人（`GET /paper/events` 的 `kind=run`）。 */
function TriggerCard(): React.ReactNode {
  const triggers = useQuery<RunTrigger[], Error>({
    queryKey: ["replay", "run-triggers"],
    queryFn: readRunTriggers,
    retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  });
  return (
    <ScreenState query={triggers} what="写入动作留痕">
      {(rows) => (
        <Card title="写入动作留痕" sub="GET /paper/events · kind=run" nopad>
          <Table
            cols={[
              { label: "受理时间" },
              { label: "交易日" },
              { label: "补跑" },
              { label: "预演" },
              { label: "操作人" },
              { label: "理由", wrap: true },
              { label: "任务", mono: true },
            ]}
            rows={rows.map((row) => ({
              key: `${row.ts_ns}-${row.job_id}`,
              cells: [ts(row.ts_ns), row.trade_date, row.catch_up ? "是" : "否", row.dry_run ? "是" : "否", row.operator, row.reason, short(row.job_id, 12)],
            }))}
            empty="还没有跑一天 / 补跑的触发记录"
          />
        </Card>
      )}
    </ScreenState>
  );
}
